/**
 * The projection behind the inspector: one `GET /plan-template/{id}/full` in, the pattern week and
 * the muscle split out.
 *
 * **The only module in this slice that names `PlanTemplateFullResponse`.** `ui/` reads
 * `InspectorView` and never the generated model — the same seam `planLibraryRow` holds for the
 * list, and the same lint rule enforces it.
 *
 * ── The pattern week is the ENTITY's ──────────────────────────────────────────────────────────
 * Which phase is drawn, which canvas the mode selects and what a day that is not there looks like
 * moved to `entities/plan-draft`'s `patternWeekOf` on 23 September 2026, when „Nowy program"'s
 * template preview became its second reader. The tests stay here, at this entry point.
 *
 * ── The OVERVIEW is the same three sentences the list row already carries, for the plan that has
 *    no list row ──────────────────────────────────────────────────────────────────────────────
 * A template's hero prints its span, its rhythm and its freshness off the row the trainer pointed
 * at, so nothing on the plate waits. A CLIENT'S plan has no such row: its list row is an assignment,
 * and `PlanAssignmentSummary` carries no phases, no cycle count and no `updatedAt`. Those three
 * facts are on this response — as the phase TREE the library's scalars were computed from — so they
 * are derived here through `planLibraryRow`'s own `planSpanOf` / `planRhythmOf` / `planActivityOf`.
 * One rule, two readings, and no way for a library row and the hero beside it to disagree about
 * what `8 tygodni` means.
 *
 * The two figures the backend computes in SQL are recomputed the same way it does:
 * `TotalCycleCount` is the sum of the phases' `cycleCount`, and `DaysPerWeek` is
 * `min(densest phase's day count, 7)` — `PlanTemplateRepository` line 91 and
 * `PlanTemplateProcessor` line 777, read in the C# rather than assumed.
 *
 * ── The counts are VALUES, and the sentences are the screen's ────────────────────────────────
 * `t` here is `TranslateKey` — a key-only translate, which is what keeps `model/` free of
 * i18next. A count cannot travel through one, so `7 ćw.` is composed by `PatternWeek` from
 * `exerciseCount`, exactly as `ListRows` composes the row's sub-line.
 *
 * No React, no DOM, no query hook.
 */

import {
  dayExerciseCount,
  patternWeekOf,
  schedulingModeIs,
  type PatternWeekView,
} from '~/entities/plan-draft';
import type { TrainWithMeModulesPlansDtosResponsesPlanTemplateFullResponse as PlanTemplateFullResponse } from '~/shared/api/generated/model';

import { toMuscleSplitView, type MuscleSplitView } from './muscleSplitView';
import {
  planActivityOf,
  planRhythmOf,
  planSpanOf,
  type PlanActivity,
  type PlanRhythm,
  type PlanShape,
  type PlanSpan,
  type TranslateKey,
} from './planLibraryRow';

/**
 * The facts a hero prints that a client's plan cannot read off its own list row.
 *
 * Every one of them is on a template's `PlanLibraryRow` already; a hero over a template reads them
 * from there and never waits. See the header for why they are derived here as well rather than in a
 * second projection.
 */
export interface PlanOverview {
  readonly span: PlanSpan | null;
  readonly rhythm: PlanRhythm | null;
  readonly activity: PlanActivity | null;
  /** Across EVERY phase, which is what the library's own `exerciseCount` counts. */
  readonly exerciseCount: number;
  /**
   * Training days in the repeat unit — `PlanShape.daysInCycle`, which is the figure `rhythm` above
   * is built from. `null` where the plan has no phases, exactly as `PlanLibraryRow.daysInCycle` is.
   *
   * It is here rather than counted off `weekTiles` at the call site because those are two different
   * questions with two different answers: the tiles draw the FIRST phase, and the rhythm is the
   * DENSEST. A hero that read one and printed the other said `5 dni / tydz.` in its sub-line and
   * `3` in the figure beside it.
   */
  readonly daysInCycle: number | null;
}

export interface InspectorView extends PatternWeekView {
  readonly muscle: MuscleSplitView;
  readonly overview: PlanOverview;
}

/**
 * The plan's shape, recomputed from the tree the way the backend computes it from the same rows.
 *
 * `TotalCycleCount` sums the phases' `cycleCount`; `DaysPerWeek` is the DENSEST phase's day count
 * capped at seven. Both were read in the C# (`PlanTemplateRepository` line 91,
 * `PlanTemplateProcessor` line 777) rather than inferred from what the list happens to return.
 */
const DAYS_IN_WEEK = 7;

function planShapeOf(response: PlanTemplateFullResponse): PlanShape {
  const phases = response.phases ?? [];
  const densest = phases.reduce((most, phase) => Math.max(most, (phase.days ?? []).length), 0);
  return {
    phaseCount: phases.length,
    weekly: schedulingModeIs(response.schedulingMode, 'date'),
    totalCycleCount: phases.reduce((sum, phase) => sum + (phase.cycleCount ?? 0), 0),
    daysInCycle: Math.min(densest, DAYS_IN_WEEK),
  };
}

function planOverviewOf(response: PlanTemplateFullResponse): PlanOverview {
  const shape = planShapeOf(response);
  return {
    span: planSpanOf(shape),
    rhythm: planRhythmOf(shape),
    activity: planActivityOf(response.updatedAt, response.createdAt),
    exerciseCount: (response.phases ?? []).reduce(
      (sum, phase) => sum + (phase.days ?? []).reduce((days, day) => days + dayExerciseCount(day), 0),
      0,
    ),
    // The row's own rule: a plan with no phases has no repeat unit to measure, and `0 dni` under a
    // figure would be a measurement rather than the absence it is.
    daysInCycle: shape.phaseCount > 0 ? shape.daysInCycle : null,
  };
}

/**
 * How many days the plan's repeat unit holds — from whichever source the SUB-LINE beside it quotes.
 *
 * **One rule for one figure, and this is where it lives.** The hero prints a day count twice: once
 * inside its sub-line, as `PlanRhythm`, and once as a figure of its own. A template quotes its list
 * row for both, because that row arrived with the screen and states the backend's own
 * `min(densest phase's day count, 7)`. A client's plan has no such row — its list row is an
 * assignment — so both come from the plan tree, through `PlanOverview`, which recomputes exactly
 * that.
 *
 * `rowRhythm` is `null` for a client's plan, which is not a special case here so much as the honest
 * answer: an assignment carries no cadence, so there is nothing to prefer over the tree.
 *
 * A row whose rhythm is a CYCLE count rather than a week — every non-DateBased mode — carries no day
 * figure at all, so that one falls to the tree as well.
 */
export function daysInRepeatUnit(
  rowRhythm: PlanRhythm | null,
  overview: PlanOverview | null,
): number | null {
  if (rowRhythm?.kind === 'week') return rowRhythm.daysPerWeek;
  return overview?.daysInCycle ?? null;
}

export function toInspectorView(
  response: PlanTemplateFullResponse,
  t: TranslateKey,
): InspectorView {
  const muscle = toMuscleSplitView(response.muscleSplit, t);
  const overview = planOverviewOf(response);
  return { ...patternWeekOf(response), muscle, overview };
}
